import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Select, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import useAppDataContext from '@/contexts/app-data/useAppDataContext';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { listDocumentTemplates } from '@/services/documentTemplates.service';
import { cn } from '@/libs/utils';
import { formatHexColor } from '@/utils/formUtils';

// Encodes/decodes a custom document template selection in the leave-type dropdown.
export const CUSTOM_TEMPLATE_PREFIX = 'customTemplate:';

const SICK_LEAVE_TYPES = [
  'sickLeave30_65',
  'sickLeave30Up_65',
  'sickLeave30_100',
  'sickLeave30Up_100',
];

export default function TypeOfLeaveForm({
  className,
  metadata,
  form,
  showLabel = true,
  modalForm = false,
  companySettings = null,
  companyId = null,
}) {
  const { queryParams, setQueryParams } = useQueryParamsContext({
    defaultValues: { typeOfLeaveSelect: 'holidayLeave' },
  });

  const { t } = useTranslation(['menus', 'forms']);

  const { enums: enumsData, isLoadingEnums } = useAppDataContext();
  const selectedTypeOfLeave = Form.useWatch('typeOfLeaveSelect', form);

  // Custom document templates the employer can generate straight from the
  // leave-type dropdown (employees never see them). Available in any consumer
  // that passes a companyId — including modals like the leave extender.
  const isEmployeeUser = Boolean(enumsData?.isEmployee);
  // Only employee-audience templates belong in the per-employee leave dropdown;
  // internal/external (company) templates are generated from the company screen.
  const { data: customTemplates = [] } = useQuery({
    queryKey: ['documentTemplates', companyId, 'employee'],
    queryFn: () => listDocumentTemplates(companyId, false, 'employee'),
    enabled: Boolean(companyId) && !isEmployeeUser,
  });

  // Templates that REPLACE the default document for a leave type are not offered
  // as standalone entries — they fire through the real leave type instead. Map
  // leaveType -> replacing template so we can flag that leave type as modified.
  const replacingByLeaveType = useMemo(() => {
    const map = {};
    for (const tpl of customTemplates) {
      if (tpl.replacesDefaultDocument && tpl.autoGenerateOnLeaveType) {
        map[tpl.autoGenerateOnLeaveType] = tpl;
      }
    }
    return map;
  }, [customTemplates]);

  // Only the standalone (non-replacing) templates appear in the "Prilagođeni
  // dokumenti" group.
  const standaloneTemplates = useMemo(
    () => customTemplates.filter((tpl) => !tpl.replacesDefaultDocument),
    [customTemplates]
  );

  const workTypesOptions = useMemo(() => {
    const list = enumsData?.companies?.workTypes ?? [];
    const isEmployee = Boolean(enumsData?.isEmployee);

    return list
      .map((item) => ({
        ...item,
        value: item.value,
        createsDocument: item.createsDocument,
        hrDropdown: item.hrDropdown,
        workType: item.workType,
      }))
      .filter((item) => {
        const baseOk =
          (item.createsDocument || item.hrDropdown) &&
          item.value !== 'paidLeave' &&
          item.value !== 'otherLeave';

        if (!baseOk) return false;

        if (
          isEmployee &&
          item?.hrDropdown === true &&
          item?.workType === false &&
          item?.dropdown !== true
        ) {
          return false;
        }

        // Filter sick leave types based on company settings
        if (SICK_LEAVE_TYPES.includes(item.key)) {
          if (!companySettings?.isSickLeaveRequest) return false;
          if (
            companySettings?.workTypes?.length &&
            !companySettings.workTypes.some((wt) => (wt.key || wt) === item.key)
          )
            return false;
        }

        // Saint day (Krsna slava) is RS market only
        if (
          item.key === 'paidLeaveSlava' &&
          companySettings?.market &&
          companySettings.market !== 'RS'
        ) {
          return false;
        }

        return true;
      });
  }, [enumsData?.companies?.workTypes, enumsData?.isEmployee, companySettings]);

  const initialValues = {
    typeOfLeaveSelect:
      !modalForm && queryParams?.typeOfLeaveSelect
        ? queryParams.typeOfLeaveSelect
        : 'holidayLeave',
  };

  useEffect(() => {
    if (queryParams?.typeOfLeaveSelect) {
      form.setFieldsValue({
        typeOfLeaveSelect: queryParams.typeOfLeaveSelect,
      });
    }
  }, [form, queryParams?.typeOfLeaveSelect]);

  const onChangeParams = (value) => {
    if (!modalForm && setQueryParams) {
      setQueryParams({ ['typeOfLeaveSelect']: value, ['action']: undefined });
    }

    form.setFieldsValue({
      typeOfLeaveSelect: value,
    });
  };

  return (
    <Form
      form={form}
      name="employee_request-typeOfLeave-form"
      initialValues={initialValues}
      layout="vertical"
      autoComplete="off"
      className={cn('', className)}
    >
      <Form.Item
        name="typeOfLeaveSelect"
        label={showLabel ? t('typeOfLeave', { ns: 'forms' }) : null}
        className={cn('!mb-0', metadata?.inputClassName)}
      >
        <Select
          mode="single"
          placement="bottomLeft"
          loading={isLoadingEnums}
          className={cn(
            'custom-select min-w-[268px]',
            metadata?.selectClassName
          )}
          onChange={onChangeParams}
          value={selectedTypeOfLeave || initialValues.typeOfLeaveSelect}
        >
          {workTypesOptions?.map((option) => {
            const { key, label, color, textColor } = option;
            const tagColor = formatHexColor(color?.argb);
            const textColorInitial = formatHexColor(textColor?.argb);
            const isModified = Boolean(replacingByLeaveType[key]);

            return (
              <Select.Option key={key} value={key}>
                <Tag
                  color={tagColor}
                  style={{
                    color: textColorInitial,
                    marginInlineEnd: 4,
                    borderRadius: 50,
                    marginBottom: 5,
                  }}
                >
                  <strong className="font-medium">{label}</strong>
                </Tag>
                {isModified && (
                  <Tag
                    color="volcano"
                    style={{ borderRadius: 50, marginBottom: 5 }}
                  >
                    {t('modifiedDocTag', {
                      ns: 'forms',
                      defaultValue: 'modifikovano',
                    })}
                  </Tag>
                )}
              </Select.Option>
            );
          })}

          {standaloneTemplates.length > 0 && (
            <Select.OptGroup
              label={t('customDocuments', {
                ns: 'forms',
                defaultValue: 'Prilagođeni dokumenti',
              })}
            >
              {standaloneTemplates.map((tpl) => (
                <Select.Option
                  key={tpl._id}
                  value={`${CUSTOM_TEMPLATE_PREFIX}${tpl._id}`}
                >
                  <Tag
                    color="purple"
                    style={{
                      marginInlineEnd: 4,
                      borderRadius: 50,
                      marginBottom: 5,
                    }}
                  >
                    <strong className="font-medium">{tpl.name}</strong>
                  </Tag>
                </Select.Option>
              ))}
            </Select.OptGroup>
          )}
        </Select>
      </Form.Item>
    </Form>
  );
}
